import { authRequired, googleAllowedDomain } from "./env";
import { cleanPreviewText } from "./format";
import type { AuthUser, AuthoritativeConcept, McpCredential, RunStatus } from "./types";

/**
 * FROZEN CONTRACT — localStorage keys.
 *
 * These six literals are a schema shared with every browser that has ever run this app.
 * Changing one strands a real user's cached runs, signs them out, or drops their MCP token.
 * `runCachePurgeVersion` in particular is a one-shot migration stamp: bumping it wipes
 * `adc-runs` for every existing user. A UI rebuild needs no schema change — do not touch it.
 *
 * `adc-theme` is read by the inline boot script in index.html (before React loads) and is
 * declared here so the full key set lives in one place.
 */
export const runStorageKey = "adc-runs";
/**
 * THE RUN THE OPERATOR OPENED. A NEW key, so no existing key changes meaning.
 *
 * The nav used to derive its Editor and Review links from `runs[0]` — the most recent run —
 * so clicking "Editor" opened SOME run without ever saying which one, or that a run had been
 * chosen at all. This key records the run the operator actually opened (by creating it, by
 * picking it in the Library, or by landing on its URL) so the chrome can NAME it. An absent
 * or unknown value means exactly one thing: no run is open, and the nav says so.
 */
export const openRunStorageKey = "adc-open-run-v1";
export const runCachePurgeKey = "adc-runs-purge-version";
export const runCachePurgeVersion = "2026-07-12-sample-run-cleanup";
export const authStorageKey = "adc-auth-user";
export const mcpCredentialStorageKey = "adc-mcp-credential-v1";
export const conceptStorageKey = "adc-authoritative-concept";
export const walkthroughStorageKey = "adc-walkthrough-seen-v1";
export const themeStorageKey = "adc-theme";

export function loadCachedRuns() {
  try {
    if (localStorage.getItem(runCachePurgeKey) !== runCachePurgeVersion) {
      localStorage.setItem(runCachePurgeKey, runCachePurgeVersion);
      localStorage.removeItem(runStorageKey);
      return [];
    }
    const cached = localStorage.getItem(runStorageKey);
    if (!cached) return [];
    const parsed = JSON.parse(cached) as RunStatus[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function cacheRuns(runs: RunStatus[]) {
  try {
    localStorage.setItem(runStorageKey, JSON.stringify(runs.slice(0, 60)));
  } catch {
    // Cache failures should never block the compiler UI.
  }
}

export function loadOpenRunId() {
  try {
    return localStorage.getItem(openRunStorageKey) || "";
  } catch {
    return "";
  }
}

export function saveOpenRunId(runId: string) {
  try {
    localStorage.setItem(openRunStorageKey, runId);
  } catch {
    // The nav degrades to "no run open", which is the honest default. It never guesses.
  }
}

export function loadAuthUser() {
  if (!authRequired) return null;
  try {
    const cached = localStorage.getItem(authStorageKey);
    if (!cached) return null;
    const user = JSON.parse(cached) as AuthUser;
    return user.email?.endsWith(`@${googleAllowedDomain}`) ? user : null;
  } catch {
    return null;
  }
}

export function loadMcpCredential() {
  try {
    const cached = localStorage.getItem(mcpCredentialStorageKey);
    if (!cached) return null;
    const credential = JSON.parse(cached) as McpCredential;
    if (!credential.token || !credential.email || Date.parse(credential.expiresAt) <= Date.now()) {
      localStorage.removeItem(mcpCredentialStorageKey);
      return null;
    }
    return credential;
  } catch {
    return null;
  }
}

export function saveMcpCredential(credential: McpCredential) {
  try {
    localStorage.setItem(mcpCredentialStorageKey, JSON.stringify(credential));
  } catch {
    // MCP remains available after another Google confirmation if local storage fails.
  }
}

export function clearMcpCredential() {
  try {
    localStorage.removeItem(mcpCredentialStorageKey);
  } catch {
    // Local token removal should not block the settings UI.
  }
}

export function loadAuthoritativeConcept(): AuthoritativeConcept {
  try {
    const cached = localStorage.getItem(conceptStorageKey);
    if (cached) {
      const parsed = JSON.parse(cached) as AuthoritativeConcept;
      if (parsed.title && parsed.filename) {
        return {
          ...parsed,
          storageMode: parsed.storageMode || (parsed.mode === "workspace" ? "metadata" : "canonical"),
          textExcerpt: parsed.textExcerpt || ""
        };
      }
    }
  } catch {
    // Keep the packaged canonical document as the fallback source.
  }
  return {
    title: "ATLAS DPI Concept Note v2",
    filename: "atlas-dpi-v2 canonical source",
    fileType: "Packaged HTML/PDF baseline",
    size: 0,
    updatedAt: "2026-07-12T00:00:00.000Z",
    mode: "canonical",
    storageMode: "canonical",
    textExcerpt: "Packaged ATLAS DPI v2 source and approved baseline package."
  };
}

export function saveAuthoritativeConcept(concept: AuthoritativeConcept) {
  try {
    localStorage.setItem(conceptStorageKey, JSON.stringify(concept));
    return true;
  } catch {
    // Settings should remain usable even when local storage is unavailable.
    return false;
  }
}

export function clearAuthoritativeConcept() {
  localStorage.removeItem(conceptStorageKey);
}

export function summarizeConcept(concept: AuthoritativeConcept): NonNullable<
  import("./types").RunForm["sourceConcept"]
> {
  return {
    title: concept.title,
    filename: concept.filename,
    fileType: concept.fileType,
    mode: concept.mode,
    size: concept.size,
    updatedAt: concept.updatedAt,
    textExcerpt: concept.textExcerpt
  };
}

export function canReadSourceText(file: File) {
  const extension = file.name.split(".").pop()?.toLowerCase() || "";
  return Boolean(
    file.type.startsWith("text/") ||
      file.type === "application/json" ||
      ["html", "htm", "md", "markdown", "txt", "json"].includes(extension)
  );
}

export function sourceTitleFromFilename(filename: string) {
  return (
    filename
      .replace(/\.(html?|pdf|docx?|md|markdown|txt|json)$/i, "")
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim() || filename
  );
}

export function sourceTitleFromText(text: string) {
  const candidates = [
    text.match(/<title[^>]*>([\s\S]*?)<\/title>/i)?.[1],
    text.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i)?.[1],
    text.match(/<meta[^>]+property=["']og:title["'][^>]+content=["']([^"']+)["']/i)?.[1]
  ];

  for (const candidate of candidates) {
    const cleaned = sourceExcerpt(candidate || "").replace(/\s+[·|-]\s+.*$/, "").trim();
    if (/atlas/i.test(cleaned) && /dpi concept note/i.test(sourceExcerpt(text))) {
      return "ATLAS DPI Concept Note";
    }
    if (cleaned.length >= 4 && cleaned.length <= 90) return cleaned;
  }
  return "";
}

export function readableFileType(file: File) {
  const extension = file.name.split(".").pop()?.toUpperCase();
  if (file.type) return file.type;
  return extension ? `${extension} file` : "Source file";
}

export function sourceExcerpt(text: string) {
  return cleanPreviewText(
    text
      .replace(/<style[\s\S]*?<\/style>/gi, " ")
      .replace(/<script[\s\S]*?<\/script>/gi, " ")
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
  );
}
